import { defineComponent as _defineComponent } from 'vue'
import { integerRange } from '@/utils/constants'
import { isEmpty, orderBy } from 'lodash'
import { computed, onMounted, reactive, ref } from 'vue'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import Inventory from '@/models/inventory'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsButton from '@/components/vision/VsButton.vue'
import { NormalizedInventory } from '@/store/modules/assets'

interface FormattedItem {
  children?: FormattedItem[]
  icon?: string
  suggested?: boolean
  title: string
  id: number | string
  inventorySubTypeId?: number
  inventoryTypeId?: number
  inventoryTypeName?: string
  inventorySubTypeName?: string
  fullName?: string
  disabled?: boolean
  header?: string
  type?: string
  divider?: boolean
}

interface Data {
  showOtherSelectableInventory: boolean
  searchValue: string | null
  selectedNode?: FormattedItem | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'JobInventoryEditField',
  props: {
    inventory: null,
    suggestedInventory: { default: () => [] },
    availableInventory: null,
    isDisabled: { type: Boolean },
    removable: { type: Boolean, default: true },
    required: { type: Boolean, default: true },
    hint: { default: null },
    subTypeErrorMessages: { default: () => [] },
    quantityErrorMessages: { default: () => [] }
  },
  emits: ["update", "remove"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
  (e: 'update', value: Inventory & { inventoryTypeName?: string })
  (e: 'remove', value: Inventory)
}), expose: any, slots: any, attrs: any }) {

const props = __props as { inventory: Inventory, suggestedInventory: number[], availableInventory: NormalizedInventory[], isDisabled: boolean, removable: boolean, required: boolean, hint: string | null, subTypeErrorMessages: string[], quantityErrorMessages: string[] }



const inventoryRules = [
  (v: string) => parseFloat(v) <= 0 && `Must be greater than 0`,
  (v: string) =>
    parseFloat(v) > integerRange.max && `Please enter a smaller number`,
  (v: string) =>
    parseFloat(v) < integerRange.min && `Please enter a larger number`,
]

const data: Data = reactive({
  showOtherSelectableInventory: false,
  searchValue: '',
  selectedNode: null,
})

const formattedItems = computed(() => {
  return orderBy(
    props.availableInventory
      .map((inv) => {
        return {
          ...inv,
          title: inv.name,
          icon: 'mdi-chevron-right',
          suggested:
            inv.id !== null ? props.suggestedInventory.includes(inv.id) : false,
          children: orderBy(inv.assetConditions, (child) =>
            child.name.toLowerCase()
          )
            .filter(
              (child) =>
                !child.selected ||
                child.id === props.inventory.inventorySubTypeId
            )
            .map((child) => ({
              ...child,
              id: `${inv.id}__${child.id}`,
              title: child.name,
              inventoryTypeName: inv.name,
              inventoryTypeId: inv.id,
              inventorySubTypeId: child.id,
              inventorySubTypeName: child.name,
              fullName: `${inv.name} ${child.name}`,
              disabled: child.disabled ?? false,
            })),
        } as FormattedItem
      })
      .filter((inv) => (inv.children?.length ?? 0) > 0),
    [(x) => !x.suggested, (x) => x.title.toLowerCase()]
  )
})

const items = computed(() => {
  const list: FormattedItem[] = [...formattedItems.value]
  if (list.some((x) => x.suggested)) {
    list.unshift({
      title: 'Suggested',
      type: 'header',
      header: 'Suggested',
    } as FormattedItem)
  }
  const lastSuggestionIndex = list.findLastIndex((x) => x.suggested)
  if (lastSuggestionIndex >= 0 && list.length > lastSuggestionIndex) {
    list.splice(
      lastSuggestionIndex + 1,
      0,
      { divider: true, type: 'divider' } as FormattedItem,
      {
        title: 'Other',
        type: 'header',
        header: 'Other',
      } as FormattedItem
    )
  }
  return list
})

const currentItems = computed(() => {
  if (data.selectedNode) {
    // Keep data.selectedNode.children[*].disabled in sync with formattedItems[*].disabled
    // formattedItems[*].disabled is reactive, but data.selectedNode.children[*].disabled is not
    const inventoryTypeId = formattedItems.value.findIndex(
      (formattedItem) => formattedItem.id === data.selectedNode?.id
    )
    if (inventoryTypeId === -1) {
      return data.selectedNode.children ?? []
    }
    for (const child of data.selectedNode.children ?? []) {
      const correspondChild = formattedItems.value[
        inventoryTypeId
      ].children?.find(
        (assetCondition) =>
          assetCondition.inventorySubTypeId === child.inventorySubTypeId
      )
      if (!correspondChild) continue
      child.disabled = correspondChild.disabled
    }
    return data.selectedNode.children ?? []
  } else {
    return items.value
  }
})

const selectableItems = computed<FormattedItem[]>(() => {
  return formattedItems.value.flatMap((inv) => inv.children || [])
})
const filteredItems = computed<FormattedItem[]>(() => {
  if (isEmpty(data.searchValue)) {
    return currentItems.value
  }
  if (data.selectedNode) {
    return currentItems.value?.filter(searchFilter)
  }
  return currentItems.value
    .flatMap((item) => item.children || [])
    .filter(searchFilter)
})

const inventoryTypeTitle = computed(() => {
  if (props.inventory.inventorySubTypeId) {
    const selectedItem = selectableItems.value.find(
      (item) => item.inventorySubTypeId === props.inventory.inventorySubTypeId
    )
    return selectedItem ? selectedItem.title : ''
  }
  return ''
})

const getLabel = computed(() => {
  return props.inventory.name ? props.inventory.name : 'Item'
})

onMounted(() => {
  if (props.inventory?.inventorySubTypeId) {
    const selectedItem = selectableItems.value.find(
      (ac) => ac.inventorySubTypeId === props.inventory.inventorySubTypeId
    )
    data.selectedNode = (formattedItems.value as FormattedItem[]).find(
      (inv) => inv.id === selectedItem?.inventoryTypeId
    )
  }
})


const updateItem = (item: FormattedItem) => {
  data.searchValue = null
  emits('update', {
    ...props.inventory,
    id: item.id,
    inventorySubTypeId: item.inventorySubTypeId ?? null,
    inventorySubTypeName: item.inventorySubTypeName ?? null,
    name: item.inventoryTypeName ?? null,
    inventoryTypeId: item.inventoryTypeId ?? null,
    inventoryTypeName: item.inventoryTypeName,
  })
}

const updateQuantity = (quantity: string) => {
  if (quantity.length === 0) return
  emits('update', { ...props.inventory, quantity: parseFloat(quantity) })
}

const removeInventory = () => {
  emits('remove', props.inventory)
}

const inventorySubtypeSelectRef = ref()

const focusInventoryType = () => {
  inventorySubtypeSelectRef.value.focus()
}

const expand = (item: FormattedItem) => {
  if (item.children) data.selectedNode = item
  focusInventoryType()
}

const collapse = () => {
  data.selectedNode = null
  data.searchValue = null
  focusInventoryType()
}

const searchFilter = (l: FormattedItem) => {
  if (l.type === 'divider' || l.type === 'header') {
    return true
  }
  const criteria = ((data.selectedNode ? l.title : l.fullName) ?? '')
    .replaceAll(' ', '')
    .toLocaleLowerCase()
  const searchVal =
    data.searchValue?.replaceAll(' ', '').toLocaleLowerCase() ?? ''

  return criteria.indexOf(searchVal) > -1
}

expose({ focusInventoryType })

return { __sfc: true,props, inventoryRules, data, formattedItems, items, currentItems, selectableItems, filteredItems, inventoryTypeTitle, getLabel, emits, updateItem, updateQuantity, removeInventory, inventorySubtypeSelectRef, focusInventoryType, expand, collapse, searchFilter, VsAutocomplete, VsHeading, VsTextInput, VsButton }
}

})